import { useEffect, useState } from 'react';
import { useAtlas } from '../app/store.ts';
import { packOf } from '../app/scene.ts';
import { scenarioById } from '../scenarios/index.ts';
import { LATE_MS, previewHref } from './preview.ts';

/** While a scene's pack loads: its name and a thin progress line in the middle of the viewport. */
export function Loading() {
  const load = useAtlas((s) => s.load);
  const engine = useAtlas((s) => s.engine);
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  // The pack loading: a chosen subject's or a view's own before the scene's (app/scene.ts packOf).
  const pack = useAtlas(packOf);
  const busy = engine.status === 'starting' || load.status === 'loading' || load.status === 'idle';
  const failed = engine.status === 'error' || load.status === 'error';
  const shown = busy || failed;
  const frac = load.total ? Math.min(1, (load.loaded ?? 0) / load.total) : null;
  const starting = engine.status === 'starting';
  const [late, setLate] = useState(false);
  useEffect(() => {
    setLate(false);
    if (!starting) return;
    const t = setTimeout(() => setLate(true), LATE_MS);
    return () => clearTimeout(t);
  }, [starting]);
  return (
    // Faded out, the text stays for the fade but leaves the accessibility tree: a screen reader never finds "Loading" on a loaded scene.
    <div className={`loading ${shown ? 'on' : ''}`} role="status" aria-live="polite" aria-hidden={!shown}>
      {failed ? (
        <>
          <div className="loading-title">Couldn’t open {scenario.name}</div>
          <div className="loading-sub">{engine.error ?? load.error}</div>
        </>
      ) : (
        <>
          <div className="loading-title">{scenario.name}</div>
          <div className="loading-sub">{engine.status === 'starting' ? 'Starting the renderer' : `Loading ${pack}`}</div>
          <div className="loading-bar">
            <div className={`loading-fill ${frac === null ? 'indeterminate' : ''}`} style={frac === null ? undefined : { width: `${frac * 100}%` }} />
          </div>
          {starting && late ? (
            <a className="loading-escape" href={previewHref()}>
              Still starting? Open the 2D preview
            </a>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Presentation mode: a quiet hint for a moment, then nothing but the scene. */
export function PresentHint() {
  const presenting = useAtlas((s) => s.presenting);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!presenting) return setVisible(false);
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 2600);
    return () => clearTimeout(t);
  }, [presenting]);
  return visible ? (
    <div className="present-hint" aria-live="polite">
      Presentation · <kbd className="kbd">P</kbd> or <kbd className="kbd">Esc</kbd> to leave
    </div>
  ) : null;
}
